import { calcTotal } from '@/helpers';
import { useGetShipmentOvertimeQuery, useGetUserPlanQuery } from '@/redux/api/api.caller';
import dashboardPageSlice, { datePickerSelector } from '@/redux/features/dashboard.slice';
import { BlockStack, InlineGrid, Button } from '@shopify/polaris';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import StatisticsCard from './components/StatisticsCard';
import { AllOrderIcon, DeliveredOrderIcon, PressingOrderIcon } from '@/icons';
import { StyledOverview } from './styled';
import DatePicker, { buildCurrentCycleRange, CURRENT_CYCLE_TITLE } from '@/components/DatePicker';
import { titleDatePickerSelector } from '@/redux/features/dashboard.slice';
import { PATH } from '@/constants';
import { OrderFilledIcon } from '@shopify/polaris-icons';
import StatisticsCardSkeleton from './components/StatisticsCardSkeleton';
import ShipmentsOvertime from './components/ShipmentsOvertime';
import RecentActivities from './components/RecentActivities';
import { CustomCard } from '@/components';
import TrakowAssistantButton from '@/components/TrakowAssistantButton';
import commonSlice from '@/redux/features/common.slice';

const Overview = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const datePicker = useSelector(datePickerSelector);
  const titleDatePicker = useSelector(titleDatePickerSelector);
  const { end, start } = datePicker;
  const isCurrentCycle = titleDatePicker === CURRENT_CYCLE_TITLE;

  const overtimeParams = isCurrentCycle
    ? { inCycle: true }
    : { fromDate: start.toISOString(), toDate: end.toISOString() };
  const { data, isFetching } = useGetShipmentOvertimeQuery(overtimeParams);

  const { data: userPlanData } = useGetUserPlanQuery({});
  const cycleStart = userPlanData?.data?.currentCycle?.cycleStart;
  const cycleEnd = userPlanData?.data?.currentCycle?.cycleEnd;
  const currentCycleRange = useMemo(() => buildCurrentCycleRange(cycleStart, cycleEnd), [cycleStart, cycleEnd]);

  const handleApplyDatePicker = (start: Date, end: Date) => dispatch(dashboardPageSlice.actions.handleDatePicker({ start, end }));
  const handleSaveDatePickerTitle = (titleBtn: string) => dispatch(dashboardPageSlice.actions.handleTitleDatePicker(titleBtn));

  const handleOpenTrakowAssistant = () => {
    // Set flag to trigger API call on Analytics page
    dispatch(commonSlice.actions.setShouldOpenTrakowAssistant(true));
    // Navigate to Analytics page
    navigate(PATH.ANALYTICS);
  };

  const shipmentStatistics = useMemo(() => {
    if (data) {
      const { all, delivered, failedAttempt, exception, expired } = data.data.statusSummary;
      return [
        {
          icon: <AllOrderIcon />,
          title: 'Total shipments',
          description: 'Number of shipments you have shipped',
          total: all,
        },
        {
          icon: <DeliveredOrderIcon />,
          title: 'Delivered shipments',
          description: 'Number of shipments delivered successfully',
          total: delivered,
        },
        {
          icon: <PressingOrderIcon />,
          title: 'Pressing shipments',
          description: 'Number of shipments might need your extra attention (incl. failed attempt, exception, expired)',
          total: calcTotal([failedAttempt, exception, expired]),
        },
      ];
    }
    return [];
  }, [data]);

  return (
    <StyledOverview>
      <CustomCard title="Overview">
        <BlockStack gap="400">
          <div className="overview-actions">
            <div className="overview-actions-left">
              <DatePicker
                titleBtn={titleDatePicker}
                {...datePicker}
                onSave={handleApplyDatePicker}
                setTitleBtn={handleSaveDatePickerTitle}
                extraRanges={currentCycleRange}
              />
              <Button variant="plain" icon={OrderFilledIcon} url={PATH.SHIPMENT}>
                View all shipments
              </Button>
            </div>
            <div className="overview-actions-right">
              <TrakowAssistantButton
                onClick={handleOpenTrakowAssistant}
                loading={false}
                className="overview-trakow-assistant"
                allowForStarter
              />
            </div>
          </div>

          <InlineGrid columns={{ xs: 1, sm: 1, md: 1, lg: 3, xl: 3 }} gap="400">
            {isFetching
              ? [0, 1, 2].map((item) => <StatisticsCardSkeleton key={item} />)
              : shipmentStatistics.length > 0 &&
                shipmentStatistics.map(({ icon, title, total, description }) => (
                  <StatisticsCard key={title} icon={icon} title={title} quantity={total} description={description} />
                ))}
          </InlineGrid>

          <InlineGrid columns={{ xs: 1, sm: 1, md: 1, lg: 2, xl: 2 }} gap="400">
            <ShipmentsOvertime />
            <RecentActivities />
          </InlineGrid>
        </BlockStack>
      </CustomCard>
    </StyledOverview>
  );
};

export default Overview;
